import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { resolveImg } from "@/lib/utils";
import TestimonialsSections from "@/components/site/TestimonialsSections";
import { MeditationCheckoutHost } from "@/components/site/MeditationCheckout";
import { inr, themeWord, ctaName, meditationActions as act, PreviewButton, SectionHead } from "@/components/site/MeditationBits";

const BeginBtn = ({ m, id, className = "" }) => (
  <button onClick={() => act.buy(m)} className={`btn-primary !py-3.5 ${className}`} data-testid={id}>Begin {ctaName(m)} — {inr(m.price_inr)}</button>
);

function ProductHero({ m }) {
  const img = resolveImg(m.image_url);
  return (
    <section className="container-ic pt-8 sm:pt-12 pb-14 sm:pb-20" data-testid="product-hero">
      <Link to="/meditations" className="inline-flex items-center gap-2 text-sm text-[#7E736E] hover:text-terracotta transition-colors" data-testid="back-to-collection"><ArrowLeft size={15} /> All meditations</Link>
      <div className="mt-6 grid lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-8 lg:gap-14 items-center">
        <div>
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-terracotta">{m.theme}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2422] leading-[1.06] mt-3 uppercase" data-testid="product-name">{m.name}</h1>
          <p className="font-serif italic text-xl sm:text-2xl text-[#7E736E] mt-2" data-testid="product-subtitle">{m.subtitle}</p>
          <p className="mt-6 text-lg sm:text-xl text-[#3D3633] leading-relaxed font-light">{m.short_line}</p>
          <p className="mt-4 text-[15px] sm:text-base text-[#5C5955] leading-relaxed">{m.description}</p>
          <p className="mt-5 font-serif text-xl sm:text-2xl italic text-[#4A3B32] leading-snug">{m.headline}</p>
          <p className="mt-6 text-sm font-medium text-[#4A3B32]" data-testid="product-meta">{[inr(m.price_inr), m.duration, m.language].filter(Boolean).join("  ·  ")}</p>
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-5">
            <BeginBtn m={m} id="hero-begin" />
            <PreviewButton m={m} />
          </div>
        </div>
        <div className="relative aspect-[16/10] sm:aspect-[4/3] lg:aspect-[4/5] rounded-3xl overflow-hidden bg-[#F3EFEA] order-first lg:order-none">
          {img ? <img src={img} alt={m.name} className="absolute inset-0 w-full h-full object-cover" data-testid="product-image" />
            : <div className="absolute inset-0 flex items-center justify-center"><span className="font-serif text-6xl sm:text-7xl text-[#E07A5F]/25">{themeWord(m.theme)}</span></div>}
        </div>
      </div>
    </section>
  );
}

function Practise({ m }) {
  if (!m.practice_points?.length) return null;
  return (
    <section className="bg-[#F3EFEA]" data-testid="practise-section">
      <div className="container-ic py-14 sm:py-20">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2D2422]">With {m.name}, practise:</h2>
        <ul className="mt-8 grid md:grid-cols-3 gap-4 sm:gap-5">
          {m.practice_points.map((p, i) => (
            <li key={i} className="soft-card !p-6 flex gap-3 items-start" data-testid={`practise-point-${i}`}>
              <span className="mt-0.5 w-7 h-7 shrink-0 rounded-full bg-[#E07A5F]/10 text-terracotta flex items-center justify-center"><Check size={15} /></span>
              <span className="text-[15px] sm:text-base text-[#3D3633] leading-relaxed">{p}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ClosingCta({ m }) {
  return (
    <section className="container-ic pb-14 sm:pb-20" data-testid="closing-cta">
      <div className="soft-card !p-7 sm:!p-12 text-left sm:text-center">
        {m.closing_heading && <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2D2422]" data-testid="closing-heading">{m.closing_heading}</h2>}
        <p className="mt-6 text-xs sm:text-sm tracking-[0.25em] uppercase text-terracotta">{m.name}</p>
        <p className="font-serif italic text-lg text-[#7E736E] mt-1">{m.subtitle}</p>
        <p className="mt-4 text-lg font-medium text-[#4A3B32]" data-testid="closing-price">{inr(m.price_inr)}  ·  One-time purchase</p>
        <BeginBtn m={m} id="closing-begin" className="mt-6 w-full sm:w-auto" />
        <p className="mt-5 text-sm text-[#7E736E]">New to meditation? That's okay. No previous experience is required.</p>
      </div>
    </section>
  );
}

function CollectionUpsell({ col, themes }) {
  if (!col) return null;
  return (
    <section className="container-ic pb-20 sm:pb-28" data-testid="collection-upsell">
      <div className="rounded-3xl bg-[#4A3B32] text-[#FDFBF7] p-7 sm:p-12 flex flex-col lg:flex-row lg:items-center gap-7 lg:justify-between">
        <div>
          <p className="font-serif text-2xl sm:text-3xl italic text-[#F3C9B9]">Different days call for different practices.</p>
          <p className="mt-4 text-xs sm:text-sm tracking-[0.3em] text-[#EBE5D9]">{themes.join("  ·  ")}</p>
          <h3 className="font-serif text-2xl sm:text-3xl mt-5 leading-tight" data-testid="upsell-collection-name">{col.name}</h3>
          <p className="mt-2 text-[15px] text-[#EBE5D9]/85">Get all four iChaitanya guided meditations in the Complete Collection.</p>
          <p className="mt-4 text-2xl"><span className="line-through text-[#EBE5D9]/60 mr-3" data-testid="upsell-combined-value">{inr(col.combined_value_inr)}</span><span className="font-semibold" data-testid="upsell-price">{inr(col.price_inr)}</span></p>
        </div>
        <button onClick={() => act.buyCollection(col)} className="btn-primary !py-4 !px-9 w-full lg:w-auto shrink-0" data-testid="upsell-get-all-four">Get All Four — {inr(col.price_inr)}</button>
      </div>
    </section>
  );
}

export default function MeditationProductPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/public/meditations").then(r => setData(r.data)).catch(() => setData({ meditations: [], collection: null }));
  }, [slug]);
  if (!data) return <div className="min-h-[60vh]" />;
  const m = data.meditations.find(x => x.slug === slug);
  if (!m) {
    return (
      <div className="container-ic py-24 min-h-[50vh]" data-testid="product-unavailable">
        <h1 className="font-serif text-4xl text-[#2D2422]">This meditation is not available right now.</h1>
        <Link to="/meditations" className="btn-ghost mt-8" data-testid="unavailable-back">View all meditations</Link>
      </div>
    );
  }
  return (
    <div className="fade-up overflow-x-hidden" data-testid={`product-page-${m.slug}`}>
      <ProductHero m={m} />
      <Practise m={m} />
      <section className="container-ic py-14 sm:py-20" data-testid="product-experiences">
        <SectionHead title="Experiences with iChaitanya" sub="Hear from people who have experienced iChaitanya meditation and guided sessions." />
        <div className="mt-8"><TestimonialsSections preview limit={2} showStatic={false} /></div>
        <Link to="/testimonials" className="btn-ghost mt-8" data-testid="product-view-more-experiences">View More Experiences <ArrowRight size={15} className="ml-2" /></Link>
      </section>
      <ClosingCta m={m} />
      <CollectionUpsell col={data.collection} themes={data.meditations.map(x => x.theme)} />
      <MeditationCheckoutHost includes={data.meditations.map(x => x.name)} />
    </div>
  );
}
